import { useCallback, useContext, useMemo, useState } from "react";
import { ReactReduxContext } from "react-redux";
import type {
  CatalogFieldProvenance,
  CatalogFieldState,
  SourceLocation as CatalogSourceLocation,
} from "@/lib/api/generated/casedb";
import { setSelectedField } from "@/store/feature/pdf/selectedFieldSlice";
import type { ResolvedHighlightLocations } from "@/lib/boundingBox/SourceLocationResolver";
import { resolveHighlightLocations } from "@/lib/boundingBox/SourceLocationResolver";
import { decodeBoundingBoxes } from "@/lib/boundingBox/BoundingBoxDecoder";
import type { AnnotationConfig } from "@/lib/boundingBox/AnnotationStrategy";
import type { SourceLocation as PdfSourceLocation } from "@/interface/pdfTypes";
import { useAgentCapabilities } from "@/features/page_agent/shared/context/AgentCapabilityContext";
import { sourceHighlightPayload, type SourceHighlightInput } from "../utils/sourceHighlightDispatch";
import { expandFieldActionPrompt } from "../utils/fieldActionPrompt";
import { fieldActionContextFor, sectionIdOfField, sectionLabelOf, type FieldAgentActions } from "../model/fieldActionContext";

export interface CatalogFieldReviewOptions {
  label: string;
  fieldId?: string;
  value?: unknown;
  state?: CatalogFieldState;
  /** When present, `highlightSource` hands it the payload instead of dispatching directly — the
   * follow-up switcher's `request`, which must load the right document before painting. */
  onSourceHighlight?: (payload: SourceHighlightInput) => void;
  /** The field's `x-agent-actions`, when the catalog declares any. */
  agentActions?: FieldAgentActions;
  /** The record the field belongs to, for a collection row. */
  recordId?: string;
  /** The field's current value as the control shows it; quoted into a prompt that asks for it. */
  displayValue?: string;
}

export interface CatalogFieldReviewBinding {
  reasoningOpen: boolean;
  setReasoningOpen(open: boolean): void;
  hasReasoning: boolean;
  hasProvenance: boolean;
  highlightSource(): void;
  /** True while the dock is mounted and can take a field action. */
  agentAvailable: boolean;
  /** True while Theragenie is working on an action this field's button started. */
  agentWorking: boolean;
  /** Hands one of the field's actions to the dock, with its prompt expanded and its context built from ids. */
  activateAgentAction(actionId: string): void;
}

/**
 * Owns the small amount of review interaction shared by every catalog control.
 *
 * The presentation primitives remain controlled and dumb. This hook resolves persisted source
 * evidence once, then lets a frame open its reasoning surface or send the same selection payload
 * the PDF viewer already understands. It intentionally has no effect/listener lifecycle: Radix
 * owns outside-click and Escape handling for the disclosure.
 */
export function useCatalogFieldReview({
  label,
  fieldId,
  value,
  state,
  onSourceHighlight,
  agentActions,
  recordId,
  displayValue,
}: CatalogFieldReviewOptions): CatalogFieldReviewBinding {
  const reduxContext = useContext(ReactReduxContext);
  const { composeFieldAction, workingField } = useAgentCapabilities();
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const provenance = state?.provenance ?? undefined;

  const locations = useMemo(
    () => resolveProvenanceLocations(provenance),
    [provenance],
  );

  const hasReasoning =
    Boolean(state?.reasoning) ||
    (state?.evidence?.length ?? 0) > 0 ||
    Boolean(provenance?.source_text) ||
    locations.boundingRegions.length > 0;

  const hasProvenance = locations.boundingRegions.length > 0;

  const highlightSource = useCallback(() => {
    if (!hasProvenance) return;

    const payload: SourceHighlightInput = { fieldId, label, value, confidence: state?.confidence, locations };
    if (onSourceHighlight) { onSourceHighlight(payload); return; }
    reduxContext?.store.dispatch(setSelectedField(sourceHighlightPayload(payload)));
  }, [fieldId, hasProvenance, label, locations, onSourceHighlight, reduxContext, state?.confidence, value]);

  const activateAgentAction = useCallback((actionId: string) => {
    if (composeFieldAction === null) throw new Error("Theragenie is not available on this screen.");
    if (agentActions === undefined || fieldId === undefined) throw new Error(`${label} declares no agent actions.`);
    const action = agentActions.actions.find((candidate) => candidate.id === actionId);
    if (action === undefined) throw new Error(`${label} declares no agent action ${actionId}.`);
    const sectionId = sectionIdOfField(fieldId);
    // An empty field has no value to quote: pass "" (not the "Not specified" display text) so the
    // prompt falls back to its own wording for an unchosen method.
    const shown = isEmptyFieldValue(value) ? "" : displayValue ?? "";
    const prompt = expandFieldActionPrompt(action.prompt, { fieldLabel: label, fieldId, sectionLabel: sectionLabelOf(sectionId), fieldValue: shown, method: shown });
    composeFieldAction(fieldActionContextFor(action, agentActions, { fieldId, fieldLabel: label, recordId }), { label: action.label, send: action.send, prompt });
  }, [agentActions, composeFieldAction, displayValue, fieldId, label, recordId, value]);

  return {
    reasoningOpen,
    setReasoningOpen,
    hasReasoning,
    hasProvenance,
    highlightSource,
    agentAvailable: composeFieldAction !== null,
    agentWorking: workingField !== null && workingField.fieldId === fieldId && workingField.recordId === (recordId ?? null),
    activateAgentAction,
  };
}

export function resolveProvenanceLocations(
  provenance: CatalogFieldProvenance | undefined,
): ResolvedHighlightLocations {
  const legacyRegions = decodeBoundingBoxes(
    provenance?.box_ids,
    provenance?.annotation_strategy_type,
    provenance?.annotation_strategy_config as AnnotationConfig | undefined,
  );
  const sourceLocations = (provenance?.source_locations ?? [])
    .map(toPdfSourceLocation)
    .filter((location): location is PdfSourceLocation => location !== undefined);
  const preferredSourceLocation = provenance?.preferred_source_location
    ? toPdfSourceLocation(provenance.preferred_source_location)
    : undefined;
  return resolveHighlightLocations(
    sourceLocations,
    preferredSourceLocation,
    legacyRegions,
  );
}

function toPdfSourceLocation(location: CatalogSourceLocation): PdfSourceLocation | undefined {
  const page = location.page;
  if (typeof page !== "number" || !Number.isInteger(page) || !Array.isArray(location.boxes)) return undefined;

  const boxes = location.boxes.flatMap((box) => {
    const { x0, y0, x1, y1 } = box;
    if (!isFiniteNumber(x0) || !isFiniteNumber(y0) || !isFiniteNumber(x1) || !isFiniteNumber(y1)) {
      return [];
    }
    return [{ x0, y0, x1, y1 }];
  });
  return boxes.length > 0 ? { page, boxes } : undefined;
}

function isFiniteNumber(value: number | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** A value the control shows as empty: nothing chosen or typed yet. */
function isEmptyFieldValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}
